import {
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend-platform/ui-lib/ui';
import { Check, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useState } from 'react';
import { useCalibrationEnvironments } from '../../../hooks/use-calibration-environments';
import { EP_BUILD_CHANGE_WARNING } from '../constants';
import type { CreateRunFormData } from '../types';

interface EnvironmentStepProps {
  formData: CreateRunFormData;
  setField: <K extends keyof CreateRunFormData>(key: K, value: CreateRunFormData[K]) => void;
}

export function EnvironmentStep({ formData, setField }: EnvironmentStepProps) {
  const [open, setOpen] = useState(false);
  const { isEP, environmentOptions, getEnvOption, resolveBotRefId } = useCalibrationEnvironments();

  const handleChange = (value: string) => {
    const botRefId = resolveBotRefId(value);
    setField('evaluationEnvironment', value);
    // manifestId of the chosen environment's active build — sent in the create-run payload.
    setField('evaluationManifestId', getEnvOption(value)?.manifestId ?? null);
    // Default the data source to the same env; the Scope step can override it independently.
    setField('historicalEnvironment', value);
    setField('evaluationBotRefId', botRefId);
    setField('historicalBotRefId', botRefId);
    // Reset downstream scope selections whenever the environment changes.
    setField('startTime', null);
    setField('endTime', null);
    setField('selectedConversationCount', null);
    setOpen(false);
  };

  const selectedOption = getEnvOption(formData.evaluationEnvironment);

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-foreground text-sm">Environment configuration</span>
        {isEP && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 shrink-0 cursor-pointer text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm text-xs">
                {EP_BUILD_CHANGE_WARNING}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="w-1/2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-border bg-background px-4 py-3 text-foreground text-sm hover:bg-muted focus:outline-none"
            >
              <span>{selectedOption?.label ?? 'Select'}</span>
              {open ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className="max-h-80 w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg border border-border p-0 shadow-sm"
          >
            {environmentOptions.length === 0 ? (
              <p className="px-4 py-3 text-muted-foreground text-xs">No environments available</p>
            ) : (
              environmentOptions.map((opt, idx) => {
                const isSelected = opt.value === formData.evaluationEnvironment;
                return (
                  <div key={opt.value}>
                    <button
                      type="button"
                      onClick={() => handleChange(opt.value)}
                      className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-muted ${isSelected ? 'bg-muted' : 'bg-background'} ${idx === 0 ? 'rounded-t-lg' : ''} ${idx === environmentOptions.length - 1 ? 'rounded-b-lg' : ''}`}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="flex items-center gap-2">
                          <span className="font-medium text-foreground text-sm">{opt.label}</span>
                          {/* Which build this env would evaluate — EP only. */}
                          {opt.buildLabel && (
                            <Badge variant="outline" className="border-border text-foreground">
                              {opt.buildLabel}
                            </Badge>
                          )}
                        </span>
                        <span className="whitespace-normal text-muted-foreground text-xs">
                          {opt.description}
                        </span>
                      </div>
                      {isSelected && (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                    </button>
                    {idx < environmentOptions.length - 1 && (
                      <div className="border-border border-b" />
                    )}
                  </div>
                );
              })
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
